<template>
  <div v-if="property" class="property-container">
    <RouterView />
  </div>
</template>

<style scoped lang="scss">
.property-container {
  height: 100%;
}
</style>

<script setup lang="ts">
import { computed, ref, watch, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { AppRouteNames, useNavigation } from '@tallo/investor/navigation';
import { usePropertyStore, usePropertyInquiriesQuery } from '@tallo/investor/property';
import { PropertyDto, PropertyStatus } from '@tallo/property';

const route = useRoute();
const propertyStore = usePropertyStore();
const propertyInquiriesQuery = usePropertyInquiriesQuery();
const router = useRouter();
const { openPropertyDashboard, openPropertyDetails } = useNavigation();
const propertyId = ref(route.params.propertyId as string);
const property = computed(() => propertyStore.property);

function redirectToCorrectPage(property: PropertyDto): void {
  const propertyId = route.params.propertyId as string;

  isPropertyActive(property) ? openPropertyDashboard(propertyId) : openPropertyDetails(propertyId);
}

function needRedirect(): boolean {
  return router.currentRoute.value.name === AppRouteNames.INVESTOR_PROPERTY;
}

function isPropertyActive(property: PropertyDto): boolean {
  return [PropertyStatus.LISTED, PropertyStatus.LISTING_IN_PROGRESS].includes(property.status);
}

watch(
  () => route.params.propertyId,
  (newPropertyId, oldPropertyId) => {
    if (newPropertyId === oldPropertyId) {
      return;
    }

    propertyStore.loadProperty(propertyId.value);
    propertyInquiriesQuery.refresh();
  },
  { immediate: true },
);

watch(
  property,
  () => {
    if (property.value) {
      if (needRedirect()) {
        redirectToCorrectPage(property.value);
      }
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  propertyStore.$reset();
});
</script>
